import * as React from 'react';

import { translate } from '~l10n/client';

import { Dialog } from '~shared/components/Dialog';

import Constants from '../../../../../shared/Constants';
import countries from '../../../../../shared/models/countries';
import states from '../../../../../shared/models/states';
import Input from '../../../../shared/components/elements/Input';
import Select from '../../../../shared/components/elements/Select';

export interface Props {
  legacyModel: any;
  legacyController: any;
  onCancel: () => void;
  onConfirm: () => void;
  open?: boolean;
}

const getCountryOptions = function (legacyModel) {
  const countryOptions = [
    {
      selected: legacyModel.country === 'US',
      title: 'USA',
      value: 'US',
    },
  ];

  for (const country of countries) {
    countryOptions.push({
      selected: country === legacyModel.country,
      title: country,
      value: country,
    });
  }

  return countryOptions;
};

const getStateOptions = function (legacyModel) {
  const stateOptions = [];

  for (const state of states) {
    stateOptions.push({
      selected: state.code === legacyModel.state,
      title: state.name,
      value: state.code,
    });
  }

  return stateOptions;
};

export function CreateSchoolModal(props: Props) {
  const { legacyModel, legacyController, onCancel, onConfirm } = props;
  const isUS = legacyModel.country === 'US';
  const isOrganization =
    legacyModel.orgType === Constants.ORGANIZATION_CORP ||
    legacyModel.orgType === Constants.ORGANIZATION_OTHER;

  const countryOptions = getCountryOptions(legacyModel);
  const stateOptions = getStateOptions(legacyModel);

  const [isValid, setIsValid] = React.useState(true);

  return (
    <Dialog
      id="create-school"
      size="medium"
      open={props.open}
      title={translate('Add Your School')}
      __customClassEscapeHatch="school-search-dialog"
      onClose={onCancel}
    >
      <div id="profile-modal-container">
        <div className="pt-8">
          <div className="form profile-form-container">
            <div className="flex flex-wrap space-y-6 sm:space-y-0 sm:space-x-8">
              <div className="flex-shrink-0 min-w-0 w-full sm:w-1/2 sm:flex-1">
                <div className="profile-select-container">
                  <label>{translate('Country')}</label>
                  <Select
                    error={legacyModel.errors.country.error}
                    label={translate('Select Your Country')}
                    onSelect={(name, value) => {
                      legacyController.countryChanged(value);
                      setIsValid(legacyModel.validateDemographics(true));
                    }}
                  >
                    {countryOptions}
                  </Select>
                </div>
              </div>

              <div className="flex-shrink-0 min-w-0 w-full sm:w-1/2 sm:flex-1">
                <Input
                  containerClass={'profile-margin-left'}
                  label={translate('City')}
                  maxLength={255}
                  type={'text'}
                  status={legacyModel.errors.city}
                  value={legacyModel.city}
                  onChange={(event) => {
                    legacyController.cityChanged(event.target.value);
                    setIsValid(legacyModel.validateDemographics(true));
                  }}
                />
              </div>
            </div>

            <div className="flex flex-wrap space-y-6 sm:space-y-0 sm:space-x-8 mt-6">
              <div className="flex-shrink-0 min-w-0 w-full sm:w-1/2 sm:flex-1">
                {isUS ? (
                  <div className="profile-select-container">
                    <p className={'demo-select-label'}>{translate('State')}</p>
                    <Select
                      error={legacyModel.errors.state.error}
                      label={translate('Select Your State')}
                      onSelect={(name, value) => {
                        legacyController.stateChanged(value);
                        setIsValid(legacyModel.validateDemographics(true));
                      }}
                    >
                      {stateOptions}
                    </Select>
                  </div>
                ) : (
                  <Input
                    label={translate('State or Province')}
                    maxLength={32}
                    type={'text'}
                    status={legacyModel.errors.state}
                    value={legacyModel.state}
                    onChange={(event) => {
                      legacyController.stateChanged(event.target.value);
                      setIsValid(legacyModel.validateDemographics(true));
                    }}
                  />
                )}
              </div>

              <div className="flex-shrink-0 min-w-0 w-full sm:w-1/2 sm:flex-1">
                <Input
                  id="zipCode"
                  containerClass={'profile-margin-left'}
                  label={
                    isUS ? translate('Zip Code') : translate('Postal Code')
                  }
                  maxLength={isUS ? 5 : 10}
                  status={legacyModel.errors.zipCode}
                  type={'text'}
                  value={legacyModel.zipCode}
                  onChange={(event) => {
                    legacyController.zipCodeChanged(event.target.value);
                    setIsValid(legacyModel.validateDemographics(true));
                  }}
                />
              </div>
            </div>

            <div className="flex flex-wrap space-y-6 sm:space-y-0 sm:space-x-8 mt-6">
              <div className="flex-shrink-0 min-w-0 w-full sm:w-1/2 sm:flex-1">
                <Input
                  label={
                    isOrganization
                      ? translate('Organization Name')
                      : translate('School Name')
                  }
                  maxLength={255}
                  type={'text'}
                  onChange={(event) => {
                    legacyController.schoolNameChanged(event.target.value);
                    setIsValid(legacyModel.validateDemographics(true));
                  }}
                  value={legacyModel.schoolName}
                  status={legacyModel.errors.schoolName}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-1 mt-32 justify-center">
            <div className="profile-action-button flex-1 sm:flex-none">
              <button
                className="secondary-action-button w-full sm:w-auto sm:min-w-60 px-4"
                onClick={onCancel}
              >
                {translate('Cancel', true)}
              </button>
            </div>
            <div className="profile-action-button flex-1 sm:flex-none ml-8">
              <button
                id={'joinSchoolButton'}
                className="action-button w-full sm:w-auto sm:min-w-60 px-4"
                disabled={!isValid}
                onClick={() => {
                  legacyModel.trimDemographics();
                  setIsValid(legacyModel.validateDemographics(true));
                  onConfirm();
                }}
              >
                {translate('Save', true)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
